import type { Surface } from './types';
import { t } from '../../i18n/I18n';

export interface Affine2 { a: number; b: number; c: number; d: number; e: number; f: number }

export function surfaceRepeat(surface: Surface | undefined, explicitTiling = false): [number, number] {
  if (explicitTiling && surface?.tiling) return [Math.abs(surface.tiling[0]) || 1, Math.abs(surface.tiling[1]) || 1];
  const tile = surface?.tileMeters ?? [1, 1];
  return [1 / Math.max(tile[0], .05), 1 / Math.max(tile[1] || tile[0], .05)];
}

/** Unity ScaleMaterial.SliderValue: zero is the authored size; ±100 is ×2 / ÷2. */
export function textureScalePercent(surface: Surface | undefined): number {
  if (!surface?.originalTileMeters || !surface.tileMeters) return 0;
  const ratio = (surface.tileMeters[0] / surface.originalTileMeters[0] + surface.tileMeters[1] / surface.originalTileMeters[1]) / 2;
  return ratio > 0 && Number.isFinite(ratio) ? Math.log2(ratio) * 100 : 0;
}

export function withTextureScalePercent(surface: Surface, percent: number): Surface {
  if (!Number.isFinite(percent)) throw new Error(t('#GeometryEnterValidTextureScale'));
  const original = surface.originalTileMeters ?? surface.tileMeters ?? [1, 1];
  const current = surface.tileMeters ?? original;
  const factor = 2 ** (Math.max(-100, Math.min(100, percent)) / 100);
  const baseTiling = surface.originalTiling ?? (surface.tiling ? [
    surface.tiling[0] * current[0] / original[0], surface.tiling[1] * current[1] / original[1],
  ] as [number, number] : undefined);
  return { ...surface, originalTileMeters: [...original], tileMeters: [original[0] * factor, original[1] * factor],
    ...(baseTiling ? { originalTiling: baseTiling, tiling: [baseTiling[0] / factor, baseTiling[1] / factor] as [number, number] } : {}),
  };
}

/** Apply a whole-item gesture to each finish's own UVs, without repainting its
 * texture, tint or PBR properties with the dominant material used by the slider. */
export function withTextureTransformDelta(surface: Surface, baseline: Surface, draft: Surface): Surface {
  const before = baseline.tileMeters ?? [1, 1], after = draft.tileMeters ?? before;
  const current = surface.tileMeters ?? [1, 1], original = surface.originalTileMeters ?? current;
  if (![...before, ...after, ...current, ...original].every(value => Number.isFinite(value) && value > 0)) {
    throw new Error(t('#GeometryEnterValidTextureScale'));
  }
  const factor = [after[0] / before[0], after[1] / before[1]];
  const baseTiling = surface.originalTiling ?? (surface.tiling ? [
    surface.tiling[0] * current[0] / original[0], surface.tiling[1] * current[1] / original[1],
  ] as [number, number] : undefined);
  const rotation = (surface.rotation ?? 0) + (draft.rotation ?? 0) - (baseline.rotation ?? 0);
  const offset: [number, number] = [
    (surface.offset?.[0] ?? 0) + (draft.offset?.[0] ?? 0) - (baseline.offset?.[0] ?? 0),
    (surface.offset?.[1] ?? 0) + (draft.offset?.[1] ?? 0) - (baseline.offset?.[1] ?? 0),
  ];
  if (![rotation, ...offset].every(Number.isFinite)) throw new Error(t('#GeometryEnterValidTextureTransform'));
  return { ...surface, originalTileMeters: [...original], tileMeters: [current[0] * factor[0], current[1] * factor[1]],
    ...(surface.tiling ? { tiling: [surface.tiling[0] / factor[0], surface.tiling[1] / factor[1]] as [number, number] } : {}),
    ...(baseTiling ? { originalTiling: [...baseTiling] as [number, number] } : {}),
    offset, rotation: ((rotation % 360) + 360) % 360 };
}

/** Unity MaterialUtils.RotateMeshUVs then _MainTex_ST: S · R(+degrees) · uv + offset. */
export function textureAffine(surface: Surface | undefined, explicitTiling = false): Affine2 {
  const [sx, sy] = surfaceRepeat(surface, explicitTiling), angle = (surface?.rotation ?? 0) * Math.PI / 180;
  const c = Math.cos(angle), s = Math.sin(angle);
  return { a: sx * c, b: sy * s, c: -sx * s, d: sy * c, e: surface?.offset?.[0] ?? 0, f: surface?.offset?.[1] ?? 0 };
}

/** Inverse UV sampling for CanvasPattern; matches slab UV=(x,-z) and its flipped image Y. */
export function floorPatternAffine(surface: Surface | undefined, imageWidth: number, imageHeight: number,
  origin: { x: number; y: number }, metresPerPixel: number): Affine2 {
  const t = textureAffine(surface), determinant = (t.a * t.d - t.b * t.c) * metresPerPixel;
  return { a: t.d / (determinant * imageWidth), b: t.b / (determinant * imageWidth),
    c: t.c / (determinant * imageHeight), d: t.a / (determinant * imageHeight),
    e: origin.x + (-t.d * t.e + t.c * t.f) / determinant,
    f: origin.y + (-t.b * t.e + t.a * t.f) / determinant };
}
